import { ComponentStore } from '@ngrx/component-store';
import { Injectable } from '@angular/core';
import { CounterDeltas, FindingVersionRef, RoiFindingProjectionService, unlabeled } from '@analysis/domain';

import { ProjectionProgressView } from '../../types';

export type CounterExclusionMode = 'complete' | 'selective';

export interface RoiStatsState {
  labelCounts: Map<string, number>;
  singleLabelCount: Map<string, number>;
  multiLabelCount: Map<string, number>;
  counterExcludedLabels: Set<string>;
  counterExclusionMode: CounterExclusionMode;
  loading: boolean;
  error: string | undefined;
  /** findingId -> the version projected, as the projection's watermarks report it. */
  projectedVersions: Map<string, FindingVersionRef>;
  /** findingId -> latest version, per the source of record. */
  expectedVersions: Map<string, FindingVersionRef>;
  /** findingId -> the version this client's own save wrote, cleared on a new generation. */
  ownSaveVersions: Map<string, FindingVersionRef>;
  /** Whether a backfill has covered this (sample, analyst). */
  projectionComplete: boolean;
  /** Whether a watermark read has landed at all yet for the current stats. */
  projectionMeasured: boolean;
  /** Whether the poll gave up on the current expectations without them being met. */
  projectionPollingExhausted: boolean;
  /** Whether the rows the view holds are known to predate the source of record. */
  viewRefreshRequired: boolean;
}

@Injectable()
export class RoiStatsStore extends ComponentStore<RoiStatsState> {
  constructor() {
    super({
      labelCounts: new Map(),
      singleLabelCount: new Map(),
      multiLabelCount: new Map(),
      counterExcludedLabels: new Set(),
      counterExclusionMode: 'complete',
      loading: true,
      error: undefined,
      projectedVersions: new Map(),
      expectedVersions: new Map(),
      ownSaveVersions: new Map(),
      projectionComplete: false,
      projectionMeasured: false,
      projectionPollingExhausted: false,
      viewRefreshRequired: false,
    });
  }

  // #region Selectors

  public readonly labelCounts$ = this.select((state) => state.labelCounts);

  // The projection's inputs, each its own selector so the reading below recomputes only
  // when one of them actually changes.
  public readonly ownSaveVersions$ = this.select((state) => state.ownSaveVersions);
  public readonly expectedVersions$ = this.select((state) => state.expectedVersions);
  private readonly projectedVersions$ = this.select((state) => state.projectedVersions);
  private readonly projectionComplete$ = this.select((state) => state.projectionComplete);
  private readonly projectionMeasured$ = this.select((state) => state.projectionMeasured);

  /**
   * How far the Mosaic projection (DynamoDB) has got through the findings the view is
   * waiting for. Undefined until a watermark read has landed for the current stats.
   *
   * `debounce` is load-bearing, not a nicety: composing five selectors means one state
   * change emits five times through `combineLatest`, and the intermediates pair the new
   * expectations with the previous measurement. Debounced, the projector only ever sees a
   * settled state - what the previous whole-state projector got for free.
   */
  public readonly projectionProgress$ = this.select(
    this.ownSaveVersions$,
    this.expectedVersions$,
    this.projectedVersions$,
    this.projectionComplete$,
    this.projectionMeasured$,
    // Composed from the five fields it reads, not from the whole state: a projector over
    // `state` re-runs this O(#findings) loop on every counter delta the analyst causes.
    (ownSaves, sourceOfRecord, projected, complete, measured): ProjectionProgressView | undefined => {
      if (!measured) return undefined;

      const progress = RoiFindingProjectionService.progress({ ownSaves, sourceOfRecord, complete }, projected);
      const { processed, total } = progress;

      return {
        processed,
        total,
        percentage: total === 0 ? 100 : Math.floor((processed / total) * 100),
        caughtUp: RoiFindingProjectionService.isProgressComplete(progress),
      };
    },
    { debounce: true }
  );

  public readonly viewRefreshRequired$ = this.select((state) => state.viewRefreshRequired);
  public readonly projectionPollingExhausted$ = this.select((state) => state.projectionPollingExhausted);
  public readonly singleLabelCount$ = this.select((state) => state.singleLabelCount);
  public readonly multiLabelCount$ = this.select((state) => state.multiLabelCount);
  public readonly counterExcludedLabels$ = this.select((state) => state.counterExcludedLabels);
  public readonly counterExclusionMode$ = this.select((state) => state.counterExclusionMode);
  public readonly loading$ = this.select((state) => state.loading, { debounce: true });
  public readonly error$ = this.select((state) => state.error);

  public readonly hasUnlabeledRois$ = this.select(this.labelCounts$, (labelCounts) => labelCounts.get(unlabeled) > 0);

  public readonly totalDetections$ = this.select(
    this.singleLabelCount$,
    this.multiLabelCount$,
    (singleLabelCount, multiLabelCount) => {
      let total = 0;
      singleLabelCount.forEach((count) => (total += count));
      multiLabelCount.forEach((count) => (total += count));
      return total;
    }
  );

  public readonly totalActiveDetections$ = this.select(
    this.singleLabelCount$,
    this.multiLabelCount$,
    this.counterExcludedLabels$,
    this.counterExclusionMode$,
    (singleLabelCount, multiLabelCount, counterExcludedLabels, counterExclusionMode) => {
      let total = 0;

      singleLabelCount.forEach((count, labelId) => {
        if (!counterExcludedLabels.has(labelId)) total += count;
      });

      multiLabelCount.forEach((count, labelCombination) => {
        if (
          RoiStatsStore.isMultiLabelActive(labelCombination.split('/'), counterExcludedLabels, counterExclusionMode)
        ) {
          total += count;
        }
      });

      return total;
    }
  );

  public readonly activeLabelCounts$ = this.select(
    this.singleLabelCount$,
    this.multiLabelCount$,
    this.counterExcludedLabels$,
    this.counterExclusionMode$,
    this.labelCounts$,
    (singleLabelCount, multiLabelCount, counterExcludedLabels, counterExclusionMode, labelCounts) => {
      const activeCounts = new Map<string, number>();

      labelCounts.forEach((_, labelId) => activeCounts.set(labelId, 0));

      singleLabelCount.forEach((count, labelId) => {
        if (counterExcludedLabels.has(labelId)) return;
        activeCounts.set(labelId, (activeCounts.get(labelId) ?? 0) + count);
      });

      multiLabelCount.forEach((count, labelCombination) => {
        const labels = labelCombination.split('/');
        if (!RoiStatsStore.isMultiLabelActive(labels, counterExcludedLabels, counterExclusionMode)) return;

        for (const labelId of labels) {
          activeCounts.set(labelId, (activeCounts.get(labelId) ?? 0) + count);
        }
      });

      if (labelCounts.has(unlabeled)) {
        activeCounts.set(unlabeled, counterExcludedLabels.has(unlabeled) ? 0 : labelCounts.get(unlabeled) ?? 0);
      }

      return activeCounts;
    }
  );

  public readonly totalLabelCount$ = this.select(
    this.labelCounts$,
    this.counterExcludedLabels$,
    (labelCounts, counterExcludedLabels) => {
      let totalCount = 0;

      labelCounts.forEach((count, labelId) => {
        if (counterExcludedLabels.has(labelId)) return;
        totalCount += count;
      });

      return totalCount;
    }
  );

  // #endregion

  // #region Updaters

  public readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading: loading,
  }));

  public readonly setLoadError = this.updater((state, error: string) => ({
    ...state,
    loading: false,
    error,
  }));

  public readonly setCounterExclusionConfig = this.updater(
    (state, config: { mode?: CounterExclusionMode; excludedLabels?: Set<string> }) => ({
      ...state,
      ...(config.mode !== undefined && { counterExclusionMode: config.mode }),
      ...(config.excludedLabels !== undefined && { counterExcludedLabels: config.excludedLabels }),
    })
  );

  public readonly setSampleStats = this.updater(
    (
      state,
      payload: {
        labelCounts: Map<string, number>;
        singleLabelCount: Map<string, number>;
        multiLabelCount: Map<string, number>;
        /** Absent from a server that predates the field; then nothing is expected. */
        findingVersions?: Map<string, FindingVersionRef>;
      }
    ) => ({
      ...state,
      labelCounts: payload.labelCounts,
      singleLabelCount: payload.singleLabelCount,
      multiLabelCount: payload.multiLabelCount,
      expectedVersions: payload.findingVersions ?? new Map(),
      loading: false,
      // New stats generation: previously-measured projection is no longer
      // comparable, so clear it until the projection-status effect re-measures.
      // `viewRefreshRequired` deliberately survives: re-reading the counters does not
      // reload the rows on screen, so what was stale about them still is.
      projectedVersions: new Map(),
      projectionComplete: false,
      projectionMeasured: false,
      projectionPollingExhausted: false,
    })
  );

  public readonly setProjectionStatus = this.updater(
    (state, status: { projectedVersions: Map<string, FindingVersionRef>; complete: boolean }) => ({
      ...state,
      projectedVersions: status.projectedVersions,
      projectionComplete: status.complete,
      projectionMeasured: true,
      // Latched, because staleness is not sticky but its consequence is: the terms clear
      // the moment the projection catches up, while the rows already loaded stay old.
      // Only a reload - or moving to another generation - makes them current.
      viewRefreshRequired:
        state.viewRefreshRequired ||
        RoiFindingProjectionService.isViewStale(
          {
            ownSaves: state.ownSaveVersions,
            sourceOfRecord: state.expectedVersions,
            complete: status.complete,
          },
          status.projectedVersions
        ),
    })
  );

  /** Set when the poll stops advancing, which is what puts the refresh action on screen. */
  public readonly setProjectionPollingExhausted = this.updater((state, projectionPollingExhausted: boolean) => ({
    ...state,
    projectionPollingExhausted,
  }));

  /**
   * The findings a local save just wrote. Not something the view waits for - the store
   * already holds those rows - but what excuses the projection from holding them yet, and
   * what tells a lagging page read that its rows are the older ones.
   *
   * Accumulates across saves rather than replacing: a second save names only its own
   * findings, and dropping the first save's would hand the projection back a lag the
   * analyst is not in fact waiting for.
   */
  public readonly recordOwnSaveVersions = this.updater((state, saved: Map<string, FindingVersionRef>) => {
    const ownSaveVersions = new Map(state.ownSaveVersions);

    for (const [findingId, ref] of saved) {
      const held = ownSaveVersions.get(findingId);
      if (!held || held.version <= ref.version) ownSaveVersions.set(findingId, ref);
    }

    return {
      ...state,
      ownSaveVersions,
      // A fresh exemption nothing has measured against yet.
      projectionMeasured: false,
      projectionPollingExhausted: false,
    };
  });

  /**
   * Moving to a different sample or analyst: nothing measured for the previous generation
   * carries over.
   *
   * Load-bearing for the own saves: those finding ids belong to the previous generation, so
   * no other partition's watermarks can ever speak about them.
   */
  public readonly resetForNewGeneration = this.updater((state) => ({
    ...state,
    ownSaveVersions: new Map<string, FindingVersionRef>(),
    viewRefreshRequired: false,
  }));

  public readonly applyCounterDeltas = this.updater((state, deltas: CounterDeltas) => {
    const labelCounts = new Map(state.labelCounts);
    const singleLabelCount = new Map(state.singleLabelCount);
    const multiLabelCount = new Map(state.multiLabelCount);

    for (const [label, delta] of deltas.labelCountDeltas)
      labelCounts.set(label, Math.max((labelCounts.get(label) ?? 0) + delta, 0));
    for (const [label, delta] of deltas.singleLabelDeltas)
      singleLabelCount.set(label, Math.max((singleLabelCount.get(label) ?? 0) + delta, 0));
    for (const [combo, delta] of deltas.multiLabelDeltas)
      multiLabelCount.set(combo, Math.max((multiLabelCount.get(combo) ?? 0) + delta, 0));

    return { ...state, labelCounts, singleLabelCount, multiLabelCount };
  });

  // #endregion

  // #region Static Helpers

  private static isMultiLabelActive(
    labels: string[],
    excludedLabels: Set<string>,
    mode: CounterExclusionMode
  ): boolean {
    return mode === 'complete'
      ? !labels.some((l) => excludedLabels.has(l))
      : !labels.every((l) => excludedLabels.has(l));
  }

  // #endregion
}
